<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
  inbox: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['submit', 'cancel']);
const MIN_SECONDS = 1;
const MAX_SECONDS = 30;

const { t } = useI18n();

const uiFlags = useMapGetter('captainInboxes/getUIFlags');
const isLoading = computed(() => uiFlags.value.updatingItem);

const state = reactive({
  debounceSeconds: props.inbox.captain_debounce_seconds ?? '',
});

const validationRules = {
  debounceSeconds: {
    inRange: helpers.withMessage(
      t('CAPTAIN.INBOXES.FORM.DEBOUNCE_SECONDS.ERROR'),
      value =>
        !value ||
        (/^\d+$/.test(value) &&
          Number(value) >= MIN_SECONDS &&
          Number(value) <= MAX_SECONDS)
    ),
  },
};

const v$ = useVuelidate(validationRules, state);

const errorMessage = computed(() =>
  v$.value.debounceSeconds.$error
    ? t('CAPTAIN.INBOXES.FORM.DEBOUNCE_SECONDS.ERROR')
    : ''
);

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', {
    assistantId: props.assistantId,
    inboxId: props.inbox.id,
    debounceSeconds: state.debounceSeconds || null,
  });
};
</script>

<template>
  <form class="flex flex-col gap-4" novalidate @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-1">
      <label
        for="debounceSeconds"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAPTAIN.INBOXES.FORM.DEBOUNCE_SECONDS.LABEL') }}
      </label>
      <Input
        id="debounceSeconds"
        v-model="state.debounceSeconds"
        type="number"
        :min="String(MIN_SECONDS)"
        :max="String(MAX_SECONDS)"
        :placeholder="t('CAPTAIN.INBOXES.FORM.DEBOUNCE_SECONDS.PLACEHOLDER')"
        :message="
          errorMessage || t('CAPTAIN.INBOXES.FORM.DEBOUNCE_SECONDS.HELP')
        "
        :message-type="errorMessage ? 'error' : 'info'"
      />
    </div>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t('CAPTAIN.FORM.EDIT')"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
